import React from 'react'
import { HttpRequest, UserInput, RuleName } from '../shared'
import { AttackStatus } from '../../attack-status'
import {
  PathTraversalSemanticAnalysisRuleSourceOverview,
  PathTraversalSemanticAnalysisRuleNoSourceOverview
} from './path-traversal-semantic-analysis-overview'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { CodeSnippet } from '../../../legacy-design-system/code-snippet'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { WithRedactedTooltip } from '../with-redacted-tooltip'
import { NewSuspiciousEventDescription } from '../attack-event-overview-view'

// Predicate to determine if the input maps to this rule
export function isPathTraversalFileSecurityBypassSemanticAnalysisProps(
  ruleUuid: string,
  detectionType?: string,
  details?: {
    readonly path?: string
  },
  callLocation?: string
) {
  return (
    ruleUuid === RuleName.PATH_TRAVERSAL_FILE_SECURITY_BYPASS &&
    detectionType === 'SEMANTIC_ANALYSIS' &&
    !!details &&
    details.hasOwnProperty('path') &&
    typeof details.path === 'string' &&
    typeof callLocation === 'string'
  )
}

// We don't know if the shape will be correct yet
type DetailsInput =
  | undefined
  | {
      readonly path?: string
    }

// The required shape to render this rule
interface DetailsValidated {
  readonly path: string
}

// Type guard to validate the shape of the details object
function isDetails(details: DetailsInput): details is DetailsValidated {
  return (
    !!details &&
    details.hasOwnProperty('path') &&
    typeof details.path === 'string'
  )
}

const containerCss = css`
  padding-bottom: 20px;
`

const resultCss = css`
  color: ${theme.color.red50};
`

/**
 * Note: this is a completely separate rule from the legacy "semantic path traversal" rule,
 * which is different than how other semantic rules are related.
 *
 * It's "finding" is "file security bypass" which is not determined from the details DTM, it is
 * just in the name of the rule.
 *
 * The only DTM details is a path string.
 */
export function PathTraversalFileSecurityBypassSemanticAnalysisOverview({
  httpRequest,
  userInput,
  details,
  sourceIP,
  callLocation,
  result
}: {
  readonly httpRequest?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
  readonly details?: {
    readonly path?: string
  }
  readonly callLocation?: string
  readonly result: AttackStatus
}) {
  if (!isDetails(details)) {
    throw new Error('details is not valid')
  }
  if (typeof callLocation !== 'string') {
    throw new Error('callLocation is invalid')
  }
  const { path } = details
  return (
    <React.Fragment>
      <PathTraversalSemanticAnalysisRuleSourceOverview
        request={httpRequest}
        sourceIP={sourceIP}
        userInput={userInput}
      />
      {path && (
        <div className={containerCss}>
          <FormattedMessage
            id="detail_attackEventResult_overviewTypeInFileOpeningCall"
            values={{
              overviewType: (
                <b>
                  <FormattedMessage id="detail_attackEventFindings_fileSecurityBypassSingular" />
                </b>
              ),
              callLocation
            }}
          />
          <CodeSnippet>
            <div className={resultCss}>
              <WithRedactedTooltip text={path} />
            </div>
          </CodeSnippet>
        </div>
      )}
      <PathTraversalSemanticAnalysisRuleNoSourceOverview sourceIP={sourceIP} />
      {result === AttackStatus.SUSPICIOUS && (
        <NewSuspiciousEventDescription
          findingTranslationId="detail_attackEventFindings_fileSecurityBypassPlural"
          containerCss={containerCss}
        />
      )}
    </React.Fragment>
  )
}
